/**
 * ============================================================
 * PGLP | MONTE HERMOM
 * COMPONENTE: ImovelParadigma_caracterizacao
 * VERSÃO: 6.1.0
 * ------------------------------------------------------------
 * NOVIDADES v6.1.0:
 *   ✔ FIX: Badge agora usa cor LOCAL (conformeCarac), não global
 *   ✔ FIX: Título "Ressalvas" só aparece quando há divergência
 *   ✔ Sem contradição visual (verde≠vermelho, título≠conteúdo)
 * ============================================================
 */

import React, { useMemo, useEffect, useCallback, useState } from 'react';
import {
  Calendar, ArrowLeft, ArrowRight,
  Database, FileText, Info, TrendingDown, Activity,
  Scale, ShieldAlert, CheckCircle2, AlertTriangle,
  Clock, Upload, RefreshCw, X, Bot, User, Link2,
  Mountain, Loader2, Sparkles, AlertCircle,
  Calculator, Delete, ShieldCheck,
} from 'lucide-react';

import { TABELA_IBAPE, TEXTO_CITACAO_LAUDO } from './TabelaIbape';
import { useTermosRegistrais } from '../../hooks/useTermosRegistrais';
import {
  classificarTermoRegistral,
  CATEGORIA_LABELS,
} from '../../utils/classificarTermoRegistral';
import useValidacaoImovel from '../../hooks/useValidacaoImovel';

/* ============================================================
   CONSTANTES
   ============================================================ */

const ESTADO_MATRICULA = {
  SEM_MATRICULA:           'SEM_MATRICULA',
  MATRICULA_SEM_TIPOLOGIA: 'MATRICULA_SEM_TIPOLOGIA',
  ANALISADA_OK:            'ANALISADA_OK',
  ANALISADA_DIVERGENTE:    'ANALISADA_DIVERGENTE',
};

const ORIGEM = {
  OCR_IA:        'OCR_IA',
  MANUAL_HUMANO: 'MANUAL_HUMANO',
  API_CARTORIO:  'API_CARTORIO',
};

const FATORES_TOPO = {
  PLANA:           1.00,
  SUAVE:           0.95,
  ACENTUADA:       0.90,
  MUITO_ACENTUADA: 0.85,
};

const LABEL_TOPO = {
  PLANA:           'Plana',
  SUAVE:           'Suave (3% a 8%)',
  ACENTUADA:       'Acentuada (8% a 20%)',
  MUITO_ACENTUADA: 'Muito acentuada (> 20%)',
};

const BADGE_CORES = {
  emerald: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400',
  amber:   'bg-amber-500/10 border-amber-500/30 text-amber-400',
  red:     'bg-red-500/10 border-red-500/30 text-red-400',
  blue:    'bg-blue-500/10 border-blue-500/30 text-blue-400',
  slate:   'bg-slate-500/10 border-slate-500/30 text-slate-400',
};

const CAMADA_LABEL = {
  PRESENCA:     'Preencher',
  FORMATO:      'Corrigir',
  CONCORDANCIA: 'Divergência',
};

/* ============================================================
   HELPERS
   ============================================================ */

function obterUsuarioAtual() {
  try {
    if (typeof window !== 'undefined' && window.MH_usuario?.nome) {
      return { nome: window.MH_usuario.nome, id: window.MH_usuario.id || 'anonimo' };
    }
    const chaves = ['usuario', 'MH_usuario', 'perito_ativo', 'peritoAtivo', 'user'];
    for (const chave of chaves) {
      const raw = localStorage.getItem(chave);
      if (!raw) continue;
      try {
        const parsed = JSON.parse(raw);
        if (parsed?.nome) return { nome: parsed.nome, id: parsed.id || 'anonimo' };
        if (typeof parsed === 'string' && parsed.trim() !== '') {
          return { nome: parsed, id: 'anonimo' };
        }
      } catch { /* segue */ }
    }
    return { nome: 'Analista', id: 'anonimo' };
  } catch {
    return { nome: 'Analista', id: 'anonimo' };
  }
}

function obterNomeArquivoMatricula(doc) {
  if (!doc) return null;
  return doc.file?.name || doc.meta?.nome || doc.meta?.name || null;
}

function matriculaFoiIngerida(doc) {
  if (!doc) return false;
  return (
    doc.status === 'pendente' ||
    doc.status === 'enviado' ||
    Boolean(doc.file) ||
    Boolean(doc.meta) ||
    Boolean(doc.url) ||
    Boolean(doc.nome)
  );
}

function numeroParaExtenso(n) {
  const mapa = {
    0:'zero',1:'um',2:'dois',3:'três',4:'quatro',5:'cinco',6:'seis',7:'sete',8:'oito',9:'nove',
    10:'dez',11:'onze',12:'doze',13:'treze',14:'quatorze',15:'quinze',16:'dezesseis',
    17:'dezessete',18:'dezoito',19:'dezenove',20:'vinte',30:'trinta',40:'quarenta',
    50:'cinquenta',60:'sessenta',70:'setenta',80:'oitenta',90:'noventa',100:'cem',
  };
  if (n in mapa) return mapa[n];
  if (n < 100) {
    const d = Math.floor(n / 10) * 10;
    const u = n % 10;
    return `${mapa[d]} e ${mapa[u]}`;
  }
  return String(n);
}

/* ============================================================
   COMPONENTE PRINCIPAL
   ============================================================ */

export default function ImovelParadigma_caracterizacao({
  formData,
  setFormData,
  onBack,
  onNext,
  imovelUuid = null,
  codigoLaudo = null,
}) {
  const { termos, loading: carregandoTermos } = useTermosRegistrais();

  /* ---------- Validação real ---------- */

  const validacao = useValidacaoImovel(formData, {
    imovelUuid:  imovelUuid || formData?.id || null,
    codigoLaudo: codigoLaudo || formData?.laudo_id || formData?.laudoRef || null,
    autoValidar: false,
  });

  const pendenciasCarac = useMemo(() => {
    const fase = validacao.por_fase?.CARACTERIZACAO;
    return fase?.pendencias || [];
  }, [validacao.por_fase]);

  const conformeCarac = pendenciasCarac.length === 0;

  /* ---------- Handler de mudança ---------- */

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value.toUpperCase() }));
  };

  /* ============================================================
     VIDA ÚTIL IBAPE
  ============================================================ */
  const vidaSugerida = useMemo(() => {
    const match = TABELA_IBAPE.find(
      (item) =>
        item.uso === formData.uso &&
        item.tipologia === formData.tipologia &&
        item.padrao === formData.padrao
    );
    return match ? match.vida : null;
  }, [formData.uso, formData.tipologia, formData.padrao]);

  const idadeNum = parseFloat(formData.idadeReal) || 0;
  const metricasVida = useMemo(() => {
    if (!vidaSugerida || vidaSugerida === 0) return { transcorrida: '0.0', restante: '100.0' };
    const transcorrida = (idadeNum / vidaSugerida) * 100;
    const restante = Math.max(0, 100 - transcorrida);
    return { transcorrida: transcorrida.toFixed(1), restante: restante.toFixed(1) };
  }, [vidaSugerida, idadeNum]);

  /* ============================================================
     ESTADO DA MATRÍCULA
  ============================================================ */
  const docMatricula         = formData.documentos?.matricula || null;
  const nomeArquivoMatricula = obterNomeArquivoMatricula(docMatricula);
  const tipologiaRegistral   = (formData.dadosMatricula?.tipologia || '').trim();

  const origem        = formData.dadosMatricula?.origem || null;
  const registradoPor = formData.dadosMatricula?.registradoPor || null;
  const registradoEm  = formData.dadosMatricula?.registradoEm || null;
  const confiancaIA   = formData.dadosMatricula?.confiancaIA ?? null;
  const observacao    = formData.dadosMatricula?.observacao || '';

  const temPdfMatricula = matriculaFoiIngerida(docMatricula);

  /* ============================================================
     CLASSIFICAÇÃO AUTOMÁTICA
  ============================================================ */
  const classificacao = useMemo(
    () => classificarTermoRegistral(tipologiaRegistral, termos),
    [tipologiaRegistral, termos]
  );

  const meta = CATEGORIA_LABELS[classificacao.categoria] || CATEGORIA_LABELS.VAZIO;

  useEffect(() => {
    if (classificacao.categoria !== 'GENERICO_COM_FINALIDADE') return;
    if (!classificacao.uso) return;
    if (formData.uso) return;
    setFormData((prev) => ({ ...prev, uso: classificacao.uso }));
  }, [classificacao.categoria, classificacao.uso, formData.uso, setFormData]);

  /* ============================================================
     ESTADO CONSOLIDADO DA MATRÍCULA
  ============================================================ */
  const estadoMatricula = useMemo(() => {
    if (!tipologiaRegistral) {
      return temPdfMatricula
        ? ESTADO_MATRICULA.MATRICULA_SEM_TIPOLOGIA
        : ESTADO_MATRICULA.SEM_MATRICULA;
    }
    if (classificacao.categoria === 'ESPECIFICO' && formData.tipologia) {
      return formData.tipologia === tipologiaRegistral
        ? ESTADO_MATRICULA.ANALISADA_OK
        : ESTADO_MATRICULA.ANALISADA_DIVERGENTE;
    }
    return ESTADO_MATRICULA.ANALISADA_OK;
  }, [tipologiaRegistral, temPdfMatricula, classificacao.categoria, formData.tipologia]);

  const temDivergencia = estadoMatricula === ESTADO_MATRICULA.ANALISADA_DIVERGENTE;

  /* ============================================================
     NARRATIVA
  ============================================================ */
  const textoDivergenciaLaudo = useMemo(() => {
    if (!temDivergencia) return '';
    return `Conforme verificado em vistoria técnica realizada no imóvel avaliando, constatou-se divergência entre a tipologia física atualmente existente e aquela descrita na respectiva matrícula imobiliária. A matrícula registra o bem como ${tipologiaRegistral}, entretanto, a situação fática observada em campo caracteriza o imóvel como ${formData.tipologia}, em função de alterações construtivas e/ou de uso não refletidas no registro imobiliário. A avaliação pericial foi desenvolvida com base na situação física real do imóvel na data da vistoria, em conformidade com os procedimentos da ABNT NBR 14.653.`;
  }, [temDivergencia, tipologiaRegistral, formData.tipologia]);

  const blocoReforma = useMemo(() => {
    const ctx = formData.contextoReforma;
    if (!ctx) return '';
    const {
      anoConstrucao, anoReforma, idadeCronologica,
      idadeAparenteAjustada, fatorRejuvenescimento,
    } = ctx;
    return (
      `\n\nRegistra-se, ainda, que o imóvel passou por reforma no ano de ${anoReforma}, ` +
      `tendo sido construído originalmente em ${anoConstrucao}, o que perfaz ` +
      `${idadeCronologica} (${numeroParaExtenso(idadeCronologica)}) anos de idade cronológica. ` +
      `Considerando-se o fator de rejuvenescimento de ${String(fatorRejuvenescimento).replace('.', ',')} ` +
      `aplicado ao período pré-reforma, estima-se a idade aparente ajustada em aproximadamente ` +
      `${String(idadeAparenteAjustada).replace('.', ',')} anos. Não obstante, para fins do cálculo da ` +
      `depreciação física pelo método Ross-Heidecke, adotou-se a idade cronológica ` +
      `(${idadeCronologica} anos), em conformidade com procedimento conservador recomendado pela ` +
      `boa prática pericial, evitando-se sobrevalorização do bem em função de benfeitorias internas.`
    );
  }, [formData.contextoReforma]);

  const narrativaFinal = useMemo(() => {
    let base = '';
    const pendente = pendenciasCarac.some((p) => p.campo === 'tipologia' || p.campo === 'vidaUtilIBAPE');

    if (temDivergencia) {
      base = textoDivergenciaLaudo;
    } else if (pendente && !tipologiaRegistral) {
      base = 'Aguardando análise da matrícula para emissão do parecer de conformidade registral. A pendência foi registrada no sistema de tarefas.';
    } else if (classificacao.categoria === 'ESPECIFICO') {
      base =
        `A matrícula registral descreve o imóvel como "${tipologiaRegistral}". ` +
        `A vistoria técnica confirmou a tipologia, mantendo-se a classificação IBAPE ` +
        `compatível com o registro.\n\n"${TEXTO_CITACAO_LAUDO}"`;
    } else if (classificacao.categoria === 'GENERICO_COM_FINALIDADE') {
      base =
        `A matrícula registral descreve o imóvel como "${tipologiaRegistral}", ` +
        `termo de natureza registral que informa a finalidade (${classificacao.uso}) ` +
        `mas não expressa a tipologia técnica conforme classificação IBAPE. ` +
        `Em vistoria, o imóvel foi classificado como ${formData.tipologia || '[a classificar]'}, ` +
        `com base em análise direta do bem, em conformidade com os procedimentos da ABNT NBR 14.653. ` +
        `Registra-se que não se trata de divergência registral, mas de complementação ` +
        `da qualificação técnica do bem.\n\n${TEXTO_CITACAO_LAUDO}`;
    } else if (classificacao.categoria === 'GENERICO_PURO') {
      base =
        `A matrícula registral descreve o imóvel genericamente como "${tipologiaRegistral}", ` +
        `termo que não expressa a tipologia técnica nem a finalidade conforme classificação IBAPE. ` +
        `Em vistoria técnica, o imóvel foi classificado como ${formData.tipologia || '[a classificar]'}, ` +
        `com base em análise direta do bem, em conformidade com os procedimentos da ABNT NBR 14.653. ` +
        `Registra-se que não se trata de divergência registral, mas de complementação ` +
        `da qualificação técnica do bem, uma vez que a matrícula não declara tipologia específica.\n\n${TEXTO_CITACAO_LAUDO}`;
    } else if (classificacao.categoria === 'SOLO') {
      base =
        `A matrícula registral descreve o imóvel como "${tipologiaRegistral}", ` +
        `termo que caracteriza a natureza do solo/terreno. A classificação técnica ` +
        `adotada no laudo considerou ${formData.tipologia || '[a classificar]'} como tipologia ` +
        `da construção existente no local, em conformidade com os procedimentos da ABNT NBR 14.653.\n\n${TEXTO_CITACAO_LAUDO}`;
    } else if (origem === ORIGEM.MANUAL_HUMANO && registradoPor) {
      const dataFmt = registradoEm
        ? new Date(registradoEm).toLocaleString('pt-BR', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
          })
        : '—';
      const blocoObs = observacao ? `\n\nObservação técnica do analista:\n"${observacao}"\n` : '';
      base =
        `A tipologia registral "${tipologiaRegistral}" foi analisada manualmente por ` +
        `${registradoPor} em ${dataFmt}, sem auxílio de ferramentas automatizadas.` +
        blocoObs +
        `\n\n${TEXTO_CITACAO_LAUDO}`;
    } else {
      base = `"${TEXTO_CITACAO_LAUDO}"`;
    }

    return base + blocoReforma;
  }, [
    temDivergencia, pendenciasCarac, classificacao.categoria, classificacao.uso,
    tipologiaRegistral, formData.tipologia, origem, registradoPor, registradoEm,
    observacao, textoDivergenciaLaudo, blocoReforma,
  ]);

  /* ============================================================
     TOPOGRAFIA
  ============================================================ */
  const [topografia, setTopografia] = useState(() => formData.topografia || null);
  const [carregandoTopo, setCarregandoTopo] = useState(false);
  const [erroTopo, setErroTopo] = useState(null);

  const inferirTopografia = useCallback(async () => {
    if (!formData.lat || !formData.lng) {
      setErroTopo('Coordenadas não disponíveis. Faça a geocodificação na Ingestão primeiro.');
      return;
    }
    setCarregandoTopo(true);
    setErroTopo(null);
    try {
      const resp = await fetch('/api/imovel/topografia.php', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lat: formData.lat, lng: formData.lng }),
      });
      const data = await resp.json();
      if (!resp.ok || !data.ok) throw new Error(data.erro || 'Falha ao consultar elevação.');
      const resultado = {
        classificacao:   data.classificacao,
        declividade_pct: data.declividade_pct,
        desnivel_m:      data.desnivel_m,
        fator:           data.fator_topografia,
        elevacoes:       data.elevacoes,
        fonte:           data.fonte,
        metodo:          data.metodo || 'INFERIDA',
        inferidoEm:      new Date().toISOString(),
      };
      setTopografia(resultado);
      setFormData((prev) => ({
        ...prev,
        topografia: resultado,
        fatorTopografia: resultado.fator,
      }));
    } catch (err) {
      setErroTopo(err.message || 'Erro desconhecido.');
    } finally {
      setCarregandoTopo(false);
    }
  }, [formData.lat, formData.lng, setFormData]);

  useEffect(() => {
    if (formData.lat && formData.lng && !topografia && !carregandoTopo) {
      inferirTopografia();
    }
  }, [formData.lat, formData.lng]); // eslint-disable-line

  /* ============================================================
     CALCULADORA DE IDADE
  ============================================================ */
  const [calculadoraAberta, setCalculadoraAberta] = useState(false);

  /* ============================================================
     SINCRONIZAÇÃO GLOBAL
  ============================================================ */
  useEffect(() => {
    setFormData((prev) => {
      const ressalva = temDivergencia ? textoDivergenciaLaudo : '';
      if (
        prev.vidaUtilIBAPE === vidaSugerida &&
        prev.percVidaTranscorrida === metricasVida.transcorrida &&
        prev.percVidaRestante === metricasVida.restante &&
        prev.ressalvaDivergencia === ressalva &&
        prev.estadoMatricula === estadoMatricula &&
        prev.narrativaCaracterizacao === narrativaFinal
      ) {
        return prev;
      }
      return {
        ...prev,
        vidaUtilIBAPE: vidaSugerida,
        percVidaTranscorrida: metricasVida.transcorrida,
        percVidaRestante: metricasVida.restante,
        ressalvaDivergencia: ressalva,
        estadoMatricula,
        narrativaCaracterizacao: narrativaFinal,
      };
    });
  }, [
    vidaSugerida, metricasVida.transcorrida, metricasVida.restante,
    temDivergencia, textoDivergenciaLaudo, estadoMatricula, narrativaFinal, setFormData,
  ]);

  /* ============================================================
     CONFIRMAÇÃO DA TIPOLOGIA REGISTRAL
  ============================================================ */
  const confirmarTipologiaRegistral = useCallback(
    (valor, origemParam = ORIGEM.MANUAL_HUMANO, obs = null) => {
      const usuarioAtual = obterUsuarioAtual();
      setFormData((prev) => ({
        ...prev,
        dadosMatricula: {
          ...(prev.dadosMatricula || {}),
          tipologia: String(valor || '').toUpperCase().trim(),
          origem: origemParam,
          registradoPor: usuarioAtual.nome,
          registradoPorId: usuarioAtual.id,
          registradoEm: new Date().toISOString(),
          confiancaIA: origemParam === ORIGEM.OCR_IA
            ? (prev.dadosMatricula?.confiancaIA ?? null)
            : null,
          observacao: obs,
        },
      }));
    },
    [setFormData]
  );

  /* ============================================================
     VALIDAÇÃO LOCAL (checklist visual)
  ============================================================ */
  const camposOk = useMemo(() => ({
    uso:       Boolean(formData.uso),
    tipologia: Boolean(formData.tipologia),
    padrao:    Boolean(formData.padrao),
    idade:     Boolean(formData.idadeReal),
    ibape:     Boolean(vidaSugerida),
  }), [formData.uso, formData.tipologia, formData.padrao, formData.idadeReal, vidaSugerida]);

  const inputBase =
    'w-full bg-slate-950 border border-white/10 rounded-xl py-3 px-4 text-[12px] font-black text-white outline-none focus:border-amber-400 transition-all placeholder:text-slate-600 placeholder:font-bold';
  const selectBase = inputBase + ' cursor-pointer appearance-none';
  const labelBase = 'text-[8px] font-black text-slate-500 uppercase ml-2 tracking-widest block mb-1';

  const usosDisponiveis = [...new Set(TABELA_IBAPE.map((i) => i.uso))];
  const tipologiasDisponiveis = [
    ...new Set(TABELA_IBAPE.filter((i) => i.uso === formData.uso).map((i) => i.tipologia)),
  ];
  const padroesDisponiveis = TABELA_IBAPE.filter(
    (i) => i.uso === formData.uso && i.tipologia === formData.tipologia
  ).map((i) => i.padrao);

  /* ============================================================
     RENDER
  ============================================================ */
  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 w-full space-y-4">

      {/* HEADER */}
      <div className="bg-slate-900 rounded-2xl px-5 py-3.5 border border-white/5 shadow-lg flex flex-col lg:flex-row justify-between gap-3 lg:items-center">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 bg-slate-950 rounded-xl flex items-center justify-center border border-white/10 shrink-0">
            <Scale className="text-amber-400" size={16} />
          </div>
          <div className="min-w-0">
            <h2 className="text-base font-black italic tracking-tight text-white uppercase leading-none">
              Diagnóstico de Conformidade
            </h2>
            <p className="text-[8px] text-slate-500 font-bold uppercase tracking-widest italic mt-1.5">
              Fase 05 · Caracterização · Confronto RGI
            </p>
          </div>
        </div>
        <div className="shrink-0 flex items-center gap-3">
          {/* Badge de status REAL (escopado à fase) — v6.1.0 */}
          <div
            className={`flex items-center gap-2 px-4 py-2 rounded-full border ${
              conformeCarac
                ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                : 'bg-rose-500/10 border-rose-500/30 text-rose-400'
            }`}
          >
            {conformeCarac
              ? <CheckCircle2 size={12} />
              : <AlertTriangle size={12} />}
            <span className="text-[8px] font-black uppercase tracking-widest">
              {conformeCarac
                ? 'Conforme'
                : `Não conforme · ${pendenciasCarac.length}`}
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-4">

        {/* COLUNA 01: SITUAÇÃO FÁTICA */}
        <div className="col-span-12 lg:col-span-4">
          <div className="bg-slate-900 p-5 rounded-2xl border border-white/5 shadow-xl space-y-3.5">
            <div className="flex items-center gap-2 pb-2.5 border-b border-white/5">
              <Database className="text-amber-400" size={14} />
              <h3 className="text-[11px] font-black text-white uppercase italic tracking-tight">
                Situação Fática
              </h3>
            </div>

            <CardMatricula
              estado={estadoMatricula}
              tipologiaRegistral={tipologiaRegistral}
              origem={origem}
              registradoPor={registradoPor}
              registradoEm={registradoEm}
              confiancaIA={confiancaIA}
              temPdf={temPdfMatricula}
              nomeArquivo={nomeArquivoMatricula}
              classificacao={classificacao}
              meta={meta}
              carregandoTermos={carregandoTermos}
              onConfirmar={confirmarTipologiaRegistral}
            />

            <div>
              <label className={labelBase}>Uso Principal *</label>
              <select name="uso" value={formData.uso || ''} onChange={handleChange} className={selectBase}>
                <option value="" className="bg-slate-900">Selecione o uso</option>
                {usosDisponiveis.map((u) => (
                  <option key={u} value={u} className="bg-slate-900">{u}</option>
                ))}
              </select>
              {classificacao.categoria === 'GENERICO_COM_FINALIDADE' && classificacao.uso && (
                <p className="text-[7.5px] text-amber-400 font-bold uppercase tracking-widest mt-1 ml-2 flex items-center gap-1">
                  <Sparkles size={9} />
                  Preenchido automaticamente pela matrícula
                </p>
              )}
            </div>

            <div>
              <label className={labelBase}>Tipologia de Campo *</label>
              <select
                name="tipologia"
                value={formData.tipologia || ''}
                onChange={handleChange}
                disabled={!formData.uso}
                className={`${selectBase} ${
                  temDivergencia ? 'border-amber-400/40 bg-amber-400/5' : ''
                } disabled:opacity-40 disabled:cursor-not-allowed`}
              >
                <option value="" className="bg-slate-900">Selecione a tipologia</option>
                {tipologiasDisponiveis.map((tipo) => (
                  <option key={tipo} value={tipo} className="bg-slate-900">{tipo}</option>
                ))}
              </select>
            </div>

            <div>
              <label className={labelBase}>Padrão Construtivo *</label>
              <select
                name="padrao"
                value={formData.padrao || ''}
                onChange={handleChange}
                disabled={!formData.tipologia}
                className={`${selectBase} disabled:opacity-40 disabled:cursor-not-allowed`}
              >
                <option value="" className="bg-slate-900">Selecione o padrão</option>
                {padroesDisponiveis.map((p) => (
                  <option key={p} value={p} className="bg-slate-900">{p}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* COLUNA 02: ANÁLISE TEMPORAL + TOPOGRAFIA */}
        <div className="col-span-12 lg:col-span-4">
          <div className="bg-slate-900 p-5 rounded-2xl border border-white/5 shadow-xl space-y-3.5">
            <div className="flex items-center gap-2 pb-2.5 border-b border-white/5">
              <Calendar className="text-amber-400" size={14} />
              <h3 className="text-[11px] font-black text-white uppercase italic tracking-tight">
                Análise Temporal
              </h3>
            </div>

            <div>
              <label className={labelBase}>Idade Real (Anos) *</label>
              <div className="relative">
                <input
                  type="number"
                  name="idadeReal"
                  value={formData.idadeReal || ''}
                  onChange={handleChange}
                  placeholder="Digite a idade"
                  className={inputBase + ' font-mono text-center pr-12'}
                />
                <button
                  type="button"
                  onClick={() => setCalculadoraAberta(true)}
                  title="Abrir calculadora de idade"
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-lg
                             bg-amber-400/10 border border-amber-400/30
                             hover:bg-amber-400 hover:border-amber-400
                             text-amber-400 hover:text-slate-950
                             flex items-center justify-center transition-all"
                >
                  <Calculator size={14} />
                </button>
              </div>
              {formData.contextoReforma && (
                <p className="text-[7.5px] text-slate-500 font-bold uppercase tracking-widest mt-1 ml-2">
                  Reforma em {formData.contextoReforma.anoReforma} · narrativa do laudo
                </p>
              )}
            </div>

            <div className="p-4 bg-slate-950 rounded-xl border border-white/5 flex flex-col items-center justify-center">
              <span className="text-[8px] font-black text-slate-500 uppercase tracking-widest mb-1">
                Vida Útil (IBAPE)
              </span>
              <span className={`text-2xl font-black italic tracking-tighter ${
                vidaSugerida ? 'text-amber-400' : 'text-slate-700 animate-pulse'
              }`}>
                {vidaSugerida ? `${vidaSugerida} ANOS` : '---'}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="bg-slate-950 p-3 rounded-xl border border-white/5 flex flex-col items-center">
                <TrendingDown size={12} className="text-slate-600 mb-1" />
                <span className="text-[7px] font-black text-slate-500 uppercase tracking-widest mb-1">
                  Transcorrida
                </span>
                <span className="text-[15px] font-mono font-black text-slate-300">
                  {metricasVida.transcorrida}%
                </span>
              </div>
              <div className="bg-amber-400 p-3 rounded-xl flex flex-col items-center shadow-lg">
                <Activity size={12} className="text-slate-950/60 mb-1" />
                <span className="text-[7px] font-black text-slate-950/60 uppercase tracking-widest mb-1">
                  Restante
                </span>
                <span className="text-[15px] font-mono font-black text-slate-950">
                  {metricasVida.restante}%
                </span>
              </div>
            </div>

            <CardTopografia
              topografia={topografia}
              carregando={carregandoTopo}
              erro={erroTopo}
              temCoordenadas={Boolean(formData.lat && formData.lng)}
              onInferir={inferirTopografia}
            />
          </div>
        </div>

        {/* COLUNA 03: PARECER + PENDÊNCIAS + AÇÕES */}
        <div className="col-span-12 lg:col-span-4 flex flex-col gap-4">
          <div className="bg-slate-900 p-5 rounded-2xl border border-white/5 shadow-xl flex-1">
            <div className="flex items-center gap-2 pb-2.5 border-b border-white/5 mb-3">
              <FileText className="text-amber-400" size={14} />
              {/* Título dinâmico — v6.1.0 */}
              <h3 className="text-[11px] font-black text-white uppercase italic tracking-tight">
                {temDivergencia ? 'Ressalvas de Laudo' : 'Parecer Técnico / Narrativa'}
              </h3>
            </div>

            <div className="p-3 bg-slate-950 rounded-xl border border-white/5 max-h-[320px] overflow-y-auto">
              <p className="text-[10px] text-slate-400 leading-relaxed font-bold italic text-justify whitespace-pre-line">
                {narrativaFinal}
              </p>
            </div>

            <div className="flex items-center gap-2 px-1 mt-3">
              <Info size={12} className="text-amber-400 shrink-0" />
              <span className="text-[7.5px] font-black text-slate-500 uppercase tracking-widest leading-tight">
                {temDivergencia
                  ? 'Alerta de divergência registrado para o resumo executivo.'
                  : conformeCarac
                    ? 'Campos desta fase conformes. Sem ressalvas.'
                    : `${pendenciasCarac.length} pendência(s) — tarefas criadas automaticamente.`}
              </span>
            </div>
          </div>

          {/* PENDÊNCIAS REAIS DA FASE */}
          <div className="bg-slate-900 p-4 rounded-2xl border border-white/5 shadow-xl space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {conformeCarac ? (
                  <CheckCircle2 size={12} className="text-emerald-500" />
                ) : (
                  <AlertTriangle size={12} className="text-amber-400" />
                )}
                <span className={`text-[9px] font-black uppercase tracking-widest ${
                  conformeCarac ? 'text-emerald-400' : 'text-amber-400'
                }`}>
                  {conformeCarac
                    ? 'Fase conforme'
                    : `${pendenciasCarac.length} pendência(s)`}
                </span>
              </div>
              <button
                type="button"
                onClick={validacao.carregarBackend}
                disabled={validacao.validando}
                className="text-[8px] font-black uppercase tracking-widest text-slate-500 hover:text-amber-400 transition-all flex items-center gap-1"
              >
                {validacao.validando ? (
                  <>
                    <Loader2 size={10} className="animate-spin" />
                    Validando
                  </>
                ) : (
                  <>
                    <RefreshCw size={10} />
                    Validar agora
                  </>
                )}
              </button>
            </div>

            {pendenciasCarac.length > 0 && (
              <div className="space-y-1.5 max-h-[180px] overflow-y-auto">
                {pendenciasCarac.map((p) => (
                  <div
                    key={p.campo}
                    className="flex items-start gap-2 p-2 rounded-lg bg-slate-950 border border-white/5"
                  >
                    <AlertCircle
                      size={10}
                      className={`shrink-0 mt-0.5 ${
                        p.camada === 'CONCORDANCIA' ? 'text-rose-500' : 'text-amber-400'
                      }`}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-[9px] font-black text-white uppercase tracking-tight leading-tight">
                        {p.label}
                      </p>
                      <p className="text-[7px] text-slate-500 font-bold uppercase tracking-wider mt-0.5">
                        {p.motivo} · {CAMADA_LABEL[p.camada] || p.camada}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* BOTÕES */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onBack}
              className="flex-1 py-3 rounded-xl bg-white/5 border border-white/10 text-white font-black text-[10px] uppercase tracking-[0.2em] hover:bg-white/10 transition-all flex items-center justify-center gap-2"
            >
              <ArrowLeft size={14} /> Voltar
            </button>
            <button
              type="button"
              onClick={onNext}
              className="flex-1 py-3 rounded-xl bg-amber-400 text-slate-950 font-black text-[10px] uppercase tracking-[0.2em] hover:bg-white transition-all flex items-center justify-center gap-2"
            >
              Avançar <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </div>

      <div className="text-center pt-2">
        <span className="text-[8px] font-black text-slate-700 uppercase tracking-[0.5em] italic">
          Monte Hermom · Luxury Valuation Systems · 2026
        </span>
      </div>

      {calculadoraAberta && (
        <ModalCalculadoraIdade
          idadeAtual={formData.idadeReal || ''}
          onFechar={() => setCalculadoraAberta(false)}
          onAplicar={({ idadeReal, contextoReforma }) => {
            setFormData((prev) => ({
              ...prev,
              idadeReal: String(idadeReal),
              contextoReforma: contextoReforma || null,
            }));
            setCalculadoraAberta(false);
          }}
        />
      )}
    </div>
  );
}

/* ============================================================
   CARD MATRÍCULA
============================================================ */

function CardMatricula({
  estado, tipologiaRegistral, origem, registradoPor, registradoEm,
  confiancaIA, temPdf, nomeArquivo, classificacao, meta, carregandoTermos, onConfirmar,
}) {
  const [editando, setEditando] = useState(false);
  const [valorTemp, setValorTemp] = useState(tipologiaRegistral || '');
  const [obsTemp, setObsTemp] = useState('');

  useEffect(() => { setValorTemp(tipologiaRegistral || ''); }, [tipologiaRegistral]);

  const salvar = () => {
    if (!valorTemp.trim()) return;
    onConfirmar(valorTemp, ORIGEM.MANUAL_HUMANO, obsTemp.trim() || null);
    setEditando(false);
    setObsTemp('');
  };

  if (estado === ESTADO_MATRICULA.SEM_MATRICULA) {
    return (
      <div className="p-3 bg-amber-400/5 rounded-xl border border-amber-400/30 space-y-2">
        <div className="flex items-center gap-2">
          <Clock size={12} className="text-amber-400" />
          <span className="text-[7px] font-black text-amber-400 uppercase tracking-widest">
            Aguardando matrícula
          </span>
        </div>
        <p className="text-[10px] font-bold text-slate-400 italic leading-relaxed">
          Nenhuma matrícula foi ingerida ainda. Uma tarefa foi criada automaticamente.
        </p>
        <div className="flex items-center gap-2 text-[8px] font-black text-slate-500 uppercase tracking-widest pt-1">
          <Upload size={10} /> Subir em: etapa Documentos
        </div>
      </div>
    );
  }

  if (estado === ESTADO_MATRICULA.MATRICULA_SEM_TIPOLOGIA) {
    return (
      <div className="p-3 bg-amber-400/5 rounded-xl border border-amber-400/30 space-y-2">
        <div className="flex items-center gap-2">
          <AlertTriangle size={12} className="text-amber-400" />
          <span className="text-[7px] font-black text-amber-400 uppercase tracking-widest">
            Análise sem IA — requer parecer humano
          </span>
        </div>
        {nomeArquivo && (
          <div className="flex items-center gap-2 px-2.5 py-1.5 bg-slate-950 rounded-md border border-white/5">
            <FileText size={10} className="text-amber-400 shrink-0" />
            <span className="text-[9px] text-slate-400 truncate">{nomeArquivo}</span>
          </div>
        )}
        <p className="text-[10px] font-bold text-slate-400 italic leading-relaxed">
          Matrícula recebida, mas a tipologia não pôde ser extraída automaticamente.
          Leia o documento, digite a tipologia registral e confirme.
        </p>
        {!editando ? (
          <button
            type="button"
            onClick={() => setEditando(true)}
            className="w-full py-2 rounded-lg bg-amber-400 text-slate-950 font-black text-[9px] uppercase tracking-widest hover:bg-white transition-all flex items-center justify-center gap-2"
          >
            <FileText size={11} /> Registrar tipologia (sem IA)
          </button>
        ) : (
          <FormEdicao
            valorTemp={valorTemp} setValorTemp={setValorTemp}
            obsTemp={obsTemp} setObsTemp={setObsTemp}
            onCancelar={() => { setEditando(false); setValorTemp(''); setObsTemp(''); }}
            onSalvar={salvar}
            classificacao={classificacao} meta={meta} carregandoTermos={carregandoTermos}
          />
        )}
      </div>
    );
  }

  const divergente = estado === ESTADO_MATRICULA.ANALISADA_DIVERGENTE;

  return (
    <div className={`p-3 rounded-xl border ${
      divergente ? 'bg-rose-950/30 border-rose-500/30' : 'bg-slate-950 border-white/5'
    }`}>
      <div className="flex items-center justify-between mb-1">
        <p className="text-[7px] font-black text-slate-500 uppercase tracking-widest">
          Tipologia na Matrícula (RGI)
        </p>
        {divergente && (
          <span className="text-[7px] font-black text-rose-400 uppercase tracking-widest flex items-center gap-1">
            <ShieldAlert size={9} /> Divergente
          </span>
        )}
      </div>

      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] font-bold text-slate-200 italic truncate">
          {tipologiaRegistral}
        </p>
        <button
          type="button"
          onClick={() => setEditando(true)}
          className="w-6 h-6 rounded-md bg-white/5 text-slate-500 hover:text-amber-400 hover:bg-white/10 transition-all flex items-center justify-center shrink-0"
          title="Reeditar"
        >
          <RefreshCw size={10} />
        </button>
      </div>

      {classificacao.categoria !== 'VAZIO' && classificacao.categoria !== 'DESCONHECIDO' && (
        <div className={`mt-1.5 px-2 py-1 rounded-md border text-[8px] font-black uppercase tracking-widest flex items-center gap-1.5 ${BADGE_CORES[meta.cor]}`}>
          <Sparkles size={9} />
          <span>{meta.label}</span>
          {classificacao.match === 'PARCIAL' && (
            <span className="opacity-70">· via "{classificacao.termoOrigem}"</span>
          )}
        </div>
      )}

      {classificacao.categoria === 'DESCONHECIDO' && !carregandoTermos && (
        <div className="mt-1.5 px-2 py-1 rounded-md border border-slate-500/30 bg-slate-500/10 text-[8px] font-black uppercase tracking-widest flex items-center gap-1.5 text-slate-400">
          <AlertCircle size={9} />
          Termo não reconhecido na base
        </div>
      )}

      {origem && (
        <div className="flex items-center gap-1.5 pt-2 mt-1.5 border-t border-white/5">
          {origem === ORIGEM.OCR_IA && (
            <>
              <Bot size={10} className="text-emerald-400" />
              <span className="text-[7px] font-black text-emerald-400 uppercase tracking-widest">IA</span>
              {confiancaIA != null && (
                <span className="text-[7px] text-emerald-400/70 font-mono">{confiancaIA}%</span>
              )}
            </>
          )}
          {origem === ORIGEM.MANUAL_HUMANO && (
            <>
              <User size={10} className="text-amber-400" />
              <span className="text-[7px] font-black text-amber-400 uppercase tracking-widest">Análise manual</span>
              {registradoPor && (
                <span className="text-[7px] text-slate-500 truncate">· {registradoPor}</span>
              )}
            </>
          )}
          {origem === ORIGEM.API_CARTORIO && (
            <>
              <Link2 size={10} className="text-blue-400" />
              <span className="text-[7px] font-black text-blue-400 uppercase tracking-widest">API Cartório</span>
            </>
          )}
          {registradoEm && (
            <span className="text-[7px] text-slate-600 font-mono ml-auto">
              {new Date(registradoEm).toLocaleString('pt-BR', {
                day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
              })}
            </span>
          )}
        </div>
      )}

      {editando && (
        <div className="mt-2 pt-2 border-t border-white/5">
          <FormEdicao
            valorTemp={valorTemp} setValorTemp={setValorTemp}
            obsTemp={obsTemp} setObsTemp={setObsTemp}
            onCancelar={() => { setEditando(false); setValorTemp(tipologiaRegistral || ''); }}
            onSalvar={salvar}
            classificacao={classificacao} meta={meta} carregandoTermos={carregandoTermos}
          />
        </div>
      )}
    </div>
  );
}

/* ============================================================
   FORM EDIÇÃO
============================================================ */

function FormEdicao({
  valorTemp, setValorTemp, obsTemp, setObsTemp,
  onCancelar, onSalvar, classificacao, meta, carregandoTermos,
}) {
  return (
    <div className="space-y-2">
      <input
        type="text"
        value={valorTemp}
        onChange={(e) => setValorTemp(e.target.value.toUpperCase())}
        placeholder="Ex: PRÉDIO RESIDENCIAL"
        autoFocus
        className="w-full bg-slate-950 border border-amber-400/30 rounded-lg px-3 py-2 text-[11px] font-bold text-slate-200 outline-none focus:border-amber-400 transition-all placeholder:text-slate-600 normal-case"
      />

      {valorTemp && valorTemp.length > 3 && (
        <div className={`px-2 py-1 rounded-md border text-[8px] font-black uppercase tracking-widest flex items-center gap-1.5 ${BADGE_CORES[meta.cor]}`}>
          <Sparkles size={9} />
          <span>{meta.label}</span>
          {classificacao.uso && <span className="opacity-70">· uso: {classificacao.uso}</span>}
          {classificacao.tipologia && <span className="opacity-70">· tipo: {classificacao.tipologia}</span>}
        </div>
      )}

      <textarea
        value={obsTemp}
        onChange={(e) => setObsTemp(e.target.value)}
        placeholder="Observação (opcional): página, trecho lido..."
        rows={2}
        className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2 text-[10px] font-medium text-slate-300 outline-none focus:border-amber-400 transition-all placeholder:text-slate-600 normal-case resize-none"
      />

      <div className="flex gap-2">
        <button
          type="button"
          onClick={onCancelar}
          className="flex-1 py-1.5 rounded-lg bg-white/5 border border-white/10 text-slate-400 font-black text-[8px] uppercase tracking-widest hover:bg-white/10 transition-all flex items-center justify-center gap-1"
        >
          <X size={10} /> Cancelar
        </button>
        <button
          type="button"
          onClick={onSalvar}
          disabled={!valorTemp.trim()}
          className="flex-1 py-1.5 rounded-lg bg-amber-400 text-slate-950 font-black text-[8px] uppercase tracking-widest hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-1"
        >
          <CheckCircle2 size={10} /> Confirmar
        </button>
      </div>
    </div>
  );
}

/* ============================================================
   CARD TOPOGRAFIA
============================================================ */

function CardTopografia({ topografia, carregando, erro, temCoordenadas, onInferir }) {
  if (!temCoordenadas) {
    return (
      <div className="p-3 bg-slate-950 rounded-xl border border-white/5 space-y-2">
        <div className="flex items-center gap-2">
          <Mountain size={12} className="text-slate-600" />
          <span className="text-[7px] font-black text-slate-500 uppercase tracking-widest">Topografia</span>
        </div>
        <p className="text-[10px] font-bold text-slate-500 italic">
          Coordenadas não disponíveis. Faça a geocodificação em Ingestão.
        </p>
      </div>
    );
  }

  if (carregando && !topografia) {
    return (
      <div className="p-3 bg-slate-950 rounded-xl border border-white/5 space-y-2">
        <div className="flex items-center gap-2">
          <Loader2 size={12} className="text-amber-400 animate-spin" />
          <span className="text-[7px] font-black text-amber-400 uppercase tracking-widest">Inferindo topografia…</span>
        </div>
        <p className="text-[9px] font-bold text-slate-500 italic">Consultando elevação SRTM 30m.</p>
      </div>
    );
  }

  if (topografia) {
    const fator = FATORES_TOPO[topografia.classificacao] || 1.00;
    return (
      <div className="p-3 bg-blue-950/30 rounded-xl border border-blue-500/30 space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Mountain size={12} className="text-blue-400" />
            <span className="text-[7px] font-black text-blue-400 uppercase tracking-widest">
              Topografia {topografia.metodo === 'INFERIDA' ? '(inferida)' : '(confirmada)'}
            </span>
          </div>
          <button
            type="button"
            onClick={onInferir}
            disabled={carregando}
            className="w-6 h-6 rounded-md bg-white/5 text-slate-500 hover:text-blue-400 hover:bg-white/10 transition-all flex items-center justify-center disabled:opacity-40"
          >
            <RefreshCw size={10} className={carregando ? 'animate-spin' : ''} />
          </button>
        </div>
        <div>
          <p className="text-[11px] font-bold text-slate-200">
            {LABEL_TOPO[topografia.classificacao] || topografia.classificacao}
          </p>
          <div className="flex items-center gap-3 mt-1 text-[8px] text-slate-500 font-mono">
            <span>Decl. {topografia.declividade_pct}%</span>
            <span>·</span>
            <span>Δ {topografia.desnivel_m}m</span>
            <span>·</span>
            <span className="text-blue-400 font-bold">Fator {fator.toFixed(2)}</span>
          </div>
        </div>
        <p className="text-[7px] text-slate-600 font-mono pt-1 border-t border-white/5">
          {topografia.fonte}
        </p>
      </div>
    );
  }

  if (erro) {
    return (
      <div className="p-3 bg-red-950/30 rounded-xl border border-red-500/30 space-y-2">
        <div className="flex items-center gap-2">
          <AlertTriangle size={12} className="text-red-400" />
          <span className="text-[7px] font-black text-red-400 uppercase tracking-widest">Falha na inferência</span>
        </div>
        <p className="text-[9px] font-bold text-red-200 leading-relaxed">{erro}</p>
        <button
          type="button"
          onClick={onInferir}
          className="w-full py-1.5 rounded-lg bg-red-500/20 text-red-300 font-black text-[8px] uppercase tracking-widest hover:bg-red-500/30 transition-all"
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  return null;
}

/* ============================================================
   MODAL CALCULADORA DE IDADE
============================================================ */

function ModalCalculadoraIdade({ idadeAtual, onFechar, onAplicar }) {
  const anoAtual = new Date().getFullYear();

  const [modo, setModo] = useState('REFORMA');
  const [anoConstrucao, setAnoConstrucao] = useState(anoAtual - 30);
  const [anoReforma,    setAnoReforma]    = useState(anoAtual - 6);
  const [idadeDireta,   setIdadeDireta]   = useState(idadeAtual || '');
  const [fator,         setFator]         = useState(0.30);

  const PRESETS_FATOR = [
    { valor: 0.20, label: '0,20', hint: 'Reforma pesada' },
    { valor: 0.30, label: '0,30', hint: 'Padrão IBAPE'  },
    { valor: 0.50, label: '0,50', hint: 'Conservador'   },
  ];

  const idadeBruta         = anoAtual - anoConstrucao;
  const idadePosReforma    = anoAtual - anoReforma;
  const idadeAntesReforma  = anoReforma - anoConstrucao;
  const idadeAparenteAjust = idadePosReforma + (idadeAntesReforma * fator);

  const idadeAplicada = useMemo(() => {
    if (modo === 'MANUAL') {
      const n = parseFloat(idadeDireta);
      return Number.isFinite(n) && n >= 0 ? Math.round(n) : null;
    }
    return idadeBruta;
  }, [modo, idadeBruta, idadeDireta]);

  const refValida   = anoReforma >= anoConstrucao && anoReforma <= anoAtual;
  const constValida = anoConstrucao >= 1800 && anoConstrucao <= anoAtual;

  const anoMin = Math.min(anoConstrucao, anoAtual - 100);
  const anoMax = anoAtual;
  const spanTotal = Math.max(1, anoMax - anoMin);
  const pctConstrucao = ((anoConstrucao - anoMin) / spanTotal) * 100;
  const pctReforma    = ((anoReforma    - anoMin) / spanTotal) * 100;

  const handleAnoConstrucao = (valor) => {
    const n = parseInt(valor, 10);
    if (Number.isFinite(n)) {
      const clamped = Math.max(1800, Math.min(anoAtual, n));
      setAnoConstrucao(clamped);
      if (anoReforma < clamped) setAnoReforma(clamped);
    } else {
      setAnoConstrucao('');
    }
  };

  const handleAnoReforma = (valor) => {
    const n = parseInt(valor, 10);
    if (Number.isFinite(n)) {
      const clamped = Math.max(anoConstrucao, Math.min(anoAtual, n));
      setAnoReforma(clamped);
    } else {
      setAnoReforma('');
    }
  };

  const aplicar = () => {
    const payload = { idadeReal: idadeAplicada, contextoReforma: null };
    if (modo === 'REFORMA' && refValida) {
      payload.contextoReforma = {
        anoConstrucao,
        anoReforma,
        idadeCronologica: idadeBruta,
        idadeAparenteAjustada: +idadeAparenteAjust.toFixed(1),
        fatorRejuvenescimento: fator,
        memoriaCalculo: {
          posReforma: idadePosReforma,
          antesReforma: idadeAntesReforma,
          formula: `${idadePosReforma} + (${idadeAntesReforma} × ${fator.toFixed(2)}) = ${idadeAparenteAjust.toFixed(1)}`,
        },
      };
    }
    onAplicar(payload);
  };

  return (
    <div className="fixed inset-0 z-[300] bg-slate-950/95 backdrop-blur-xl flex items-center justify-center p-4">
      <div className="bg-slate-900 w-full max-w-2xl rounded-[2.5rem] border border-white/10 shadow-2xl overflow-hidden max-h-[94vh] flex flex-col">

        <div className="bg-slate-950 p-5 flex justify-between items-center border-b border-white/5 shrink-0">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-amber-400 rounded-xl flex items-center justify-center">
              <Calculator size={18} className="text-slate-950" />
            </div>
            <div>
              <h3 className="text-white text-[14px] font-black uppercase italic tracking-tight leading-none">
                Calculadora de Idade
              </h3>
              <p className="text-[8px] text-amber-400/70 font-black uppercase tracking-[0.3em] mt-1">
                Cronológica · Base do cálculo
              </p>
            </div>
          </div>
          <button
            onClick={onFechar}
            className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-slate-400 hover:text-white transition-all"
          >
            <X size={14} />
          </button>
        </div>

        <div className="bg-amber-400/5 border-b border-amber-400/20 px-5 py-3 shrink-0">
          <p className="text-[9px] text-amber-400/90 leading-relaxed">
            ⓘ <strong>Idade aplicada ao cálculo = cronológica</strong> (ano atual − construção).
            O fator de reforma é <strong>apenas informativo</strong> e vai para a narrativa do laudo.
          </p>
        </div>

        <div className="p-5 pb-3 shrink-0">
          <div className="flex gap-2">
            {[
              { id: 'ANO_CONSTRUCAO', label: 'Sem Reforma' },
              { id: 'REFORMA',        label: 'Com Reforma' },
              { id: 'MANUAL',         label: 'Direto'      },
            ].map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => setModo(opt.id)}
                className={`flex-1 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all ${
                  modo === opt.id
                    ? 'bg-amber-400 text-slate-950 shadow-lg'
                    : 'bg-white/5 text-slate-400 hover:bg-white/10'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="px-5 pb-5 space-y-4 overflow-y-auto flex-1">

          {modo === 'ANO_CONSTRUCAO' && (
            <div className="bg-slate-950 rounded-2xl border border-white/5 p-4 space-y-3">
              <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block">
                Ano de Construção
              </label>
              <input
                type="number"
                value={anoConstrucao}
                onChange={(e) => handleAnoConstrucao(e.target.value)}
                placeholder={`Ex: ${anoAtual - 30}`}
                autoFocus
                inputMode="numeric"
                className="w-full bg-slate-900 border border-white/10 rounded-xl px-4 py-3
                           text-[18px] font-mono font-black text-white text-center
                           outline-none focus:border-amber-400 transition-all
                           placeholder:text-slate-600
                           [appearance:textfield]
                           [&::-webkit-outer-spin-button]:appearance-none
                           [&::-webkit-inner-spin-button]:appearance-none"
              />
              <div className="flex items-center justify-between bg-slate-900/60 rounded-lg px-3 py-2">
                <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest">
                  Idade Aplicada
                </span>
                <span className="text-[14px] font-mono font-black text-amber-400">
                  {constValida ? `${idadeBruta} anos` : '—'}
                </span>
              </div>
            </div>
          )}

          {modo === 'REFORMA' && (
            <>
              <div className="bg-slate-950 rounded-2xl border border-white/5 p-4 space-y-3">
                <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block">
                  Datas
                </span>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <label className="text-[8px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                      Ano Construção
                    </label>
                    <input
                      type="number"
                      value={anoConstrucao}
                      onChange={(e) => handleAnoConstrucao(e.target.value)}
                      inputMode="numeric"
                      className="w-full bg-slate-900 border border-white/10 rounded-xl px-3 py-2.5
                                 text-[16px] font-mono font-black text-white text-center
                                 outline-none focus:border-slate-400 transition-all
                                 [appearance:textfield]
                                 [&::-webkit-outer-spin-button]:appearance-none
                                 [&::-webkit-inner-spin-button]:appearance-none"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[8px] font-black text-amber-400 uppercase tracking-widest flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                      Ano Reforma
                    </label>
                    <input
                      type="number"
                      value={anoReforma}
                      onChange={(e) => handleAnoReforma(e.target.value)}
                      inputMode="numeric"
                      className="w-full bg-slate-900 border border-white/10 rounded-xl px-3 py-2.5
                                 text-[16px] font-mono font-black text-amber-400 text-center
                                 outline-none focus:border-amber-400 transition-all
                                 [appearance:textfield]
                                 [&::-webkit-outer-spin-button]:appearance-none
                                 [&::-webkit-inner-spin-button]:appearance-none"
                    />
                  </div>
                </div>
              </div>

              <div className="bg-slate-950 rounded-2xl border border-white/5 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
                    Linha do Tempo
                  </span>
                  <span className="text-[8px] font-mono text-slate-600">
                    {anoMin} → {anoMax}
                  </span>
                </div>
                <div className="relative h-16 select-none">
                  <div className="absolute inset-x-0 top-7 h-1 rounded-full bg-slate-800" />
                  <div
                    className="absolute top-7 h-1 rounded-l-full bg-slate-600"
                    style={{
                      left: `${pctConstrucao}%`,
                      width: `${Math.max(0, pctReforma - pctConstrucao)}%`,
                    }}
                  />
                  <div
                    className="absolute top-7 h-1 rounded-r-full bg-amber-400"
                    style={{
                      left: `${pctReforma}%`,
                      width: `${Math.max(0, 100 - pctReforma)}%`,
                    }}
                  />
                  <div
                    className="absolute -translate-x-1/2 flex flex-col items-center"
                    style={{ left: `${pctConstrucao}%` }}
                  >
                    <span className="text-[7px] font-black text-slate-400 uppercase tracking-widest mb-1">
                      Construção
                    </span>
                    <div className="w-4 h-4 rounded-full bg-slate-400 border-2 border-slate-950" />
                    <span className="text-[10px] font-mono font-black text-white mt-1">
                      {anoConstrucao}
                    </span>
                  </div>
                  <div
                    className="absolute -translate-x-1/2 flex flex-col items-center z-20"
                    style={{ left: `${pctReforma}%` }}
                  >
                    <span className="text-[7px] font-black text-amber-400 uppercase tracking-widest mb-1">
                      Reforma
                    </span>
                    <div className="w-5 h-5 rounded-full bg-amber-400 border-2 border-slate-950 shadow-[0_0_15px_rgba(245,158,11,0.5)]" />
                    <span className="text-[10px] font-mono font-black text-amber-400 mt-1">
                      {anoReforma}
                    </span>
                  </div>
                  <div
                    className="absolute -translate-x-1/2 right-0 flex flex-col items-center"
                    style={{ left: '100%' }}
                  >
                    <span className="text-[7px] font-black text-slate-500 uppercase tracking-widest mb-1">
                      Hoje
                    </span>
                    <div className="w-3 h-3 rounded-full bg-slate-600 border-2 border-slate-950" />
                  </div>
                </div>
              </div>

              <div className="bg-slate-950 rounded-2xl border border-white/5 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
                      Fator de Rejuvenescimento
                    </span>
                    <span className="bg-slate-700 text-slate-300 text-[7px] px-1.5 py-0.5 rounded font-black uppercase tracking-widest">
                      Informativo
                    </span>
                  </div>
                  <span className="text-[22px] font-mono font-black text-slate-400 leading-none">
                    {fator.toFixed(2).replace('.', ',')}
                  </span>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  {PRESETS_FATOR.map((p) => (
                    <button
                      key={p.valor}
                      type="button"
                      onClick={() => setFator(p.valor)}
                      className={`py-2.5 rounded-lg border text-center transition-all ${
                        Math.abs(fator - p.valor) < 0.01
                          ? 'bg-slate-700 border-slate-600 text-white'
                          : 'bg-white/5 border-white/10 text-slate-500 hover:bg-white/10'
                      }`}
                    >
                      <span className="block text-[12px] font-mono font-black leading-none">
                        {p.label}
                      </span>
                      <span className={`block text-[7px] font-black uppercase tracking-widest mt-1 ${
                        Math.abs(fator - p.valor) < 0.01 ? 'text-slate-400' : 'text-slate-600'
                      }`}>
                        {p.hint}
                      </span>
                    </button>
                  ))}
                </div>

                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={fator}
                  onChange={(e) => setFator(parseFloat(e.target.value))}
                  className="w-full h-2 rounded-full appearance-none cursor-pointer bg-slate-800"
                />
                <div className="flex justify-between text-[7px] font-black uppercase tracking-widest">
                  <span className="text-slate-500">0,00 · Reforma total</span>
                  <span className="text-slate-500">1,00 · Sem efeito</span>
                </div>
              </div>

              <div className="bg-slate-950 rounded-2xl border border-amber-400/30 p-4 space-y-3">
                <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block">
                  Resumo
                </span>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-slate-900 border border-amber-400/40 rounded-xl p-3 space-y-1">
                    <span className="text-[7.5px] font-black text-amber-400 uppercase tracking-widest">
                      Aplicada ao cálculo
                    </span>
                    <div className="text-[28px] font-mono font-black text-amber-400 leading-none pt-1">
                      {constValida ? idadeBruta : '—'}
                      <span className="text-[11px] text-amber-400/60 ml-1">anos</span>
                    </div>
                    <p className="text-[7px] text-slate-500 leading-tight">
                      {anoAtual} − {anoConstrucao} = {idadeBruta} anos
                    </p>
                  </div>
                  <div className="bg-slate-900 border border-slate-600/40 rounded-xl p-3 space-y-1">
                    <span className="text-[7.5px] font-black text-slate-400 uppercase tracking-widest">
                      Idade aparente (anexo)
                    </span>
                    <div className="text-[28px] font-mono font-black text-slate-400 leading-none pt-1">
                      {refValida ? idadeAparenteAjust.toFixed(1).replace('.', ',') : '—'}
                      <span className="text-[11px] text-slate-500 ml-1">anos</span>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {modo === 'MANUAL' && (
            <div className="bg-slate-950 rounded-2xl border border-white/5 p-4 space-y-3">
              <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block">
                Idade Aparente (anos)
              </label>
              <input
                type="number"
                value={idadeDireta}
                onChange={(e) => setIdadeDireta(e.target.value)}
                placeholder="Ex: 41"
                autoFocus
                inputMode="numeric"
                className="w-full bg-slate-900 border border-white/10 rounded-xl px-4 py-3
                           text-[20px] font-mono font-black text-white text-center
                           outline-none focus:border-amber-400 transition-all"
              />
            </div>
          )}
        </div>

        <div className="p-5 border-t border-white/5 bg-slate-950/50 flex gap-3 shrink-0">
          <button
            type="button"
            onClick={() => {
              setAnoConstrucao(anoAtual - 30);
              setAnoReforma(anoAtual - 6);
              setIdadeDireta('');
              setFator(0.30);
            }}
            className="px-4 py-3 rounded-xl bg-white/5 text-slate-400 hover:bg-white/10
                       text-[10px] font-black uppercase tracking-widest transition-all
                       flex items-center gap-2"
          >
            <Delete size={12} /> Limpar
          </button>
          <button
            type="button"
            disabled={idadeAplicada === null || (modo === 'REFORMA' && !refValida)}
            onClick={aplicar}
            className="flex-1 py-3 rounded-xl bg-amber-400 text-slate-950
                       hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed
                       text-[10px] font-black uppercase tracking-widest transition-all
                       flex items-center justify-center gap-2"
          >
            <CheckCircle2 size={12} />
            {idadeAplicada !== null && (modo !== 'REFORMA' || refValida)
              ? `Aplicar ${idadeAplicada} anos`
              : 'Aguardando dados'}
          </button>
        </div>
      </div>
    </div>
  );
}